import { useState, type ReactNode } from 'react'
import { toast } from '@/lib/toast'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import type { RelationRef } from '../timeline/types'
import { getOwnerTransfer } from './api'
import { OwnerTransferDialog } from './OwnerTransferDialog'
import type { OwnerTransfer } from './types'

/**
 * Changing the owner of a relation (ED-33): when it has agreements or open tasks of the old owner,
 * ask whether to hand them over too; otherwise (and when the question cannot be asked) change the
 * owner at once. `save` patches the relation with `owner_user_id` and `transfer_agreements`.
 */
export function useOwnerChange(
  relation: RelationRef,
  hasOwner: boolean,
  save: (fields: { owner_user_id: string | null; transfer_agreements: boolean }) => Promise<void>,
): { changeOwner: (ownerId: string | null) => void; dialog: ReactNode } {
  const { membersById } = useTeamMembers()
  const [pending, setPending] = useState<{ ownerId: string | null; transfer: OwnerTransfer } | null>(null)

  function changeOwner(ownerId: string | null) {
    if (!hasOwner || ownerId === null) {
      void save({ owner_user_id: ownerId, transfer_agreements: false })
      return
    }
    getOwnerTransfer(relation)
      .then((transfer) => {
        if (transfer.agreements + transfer.tasks === 0) void save({ owner_user_id: ownerId, transfer_agreements: false })
        else setPending({ ownerId, transfer })
      })
      .catch(() => {
        toast.error('Kon niet nagaan of afspraken en taken mee moeten; alleen de eigenaar is gewijzigd.')
        void save({ owner_user_id: ownerId, transfer_agreements: false })
      })
  }

  const dialog = pending ? (
    <OwnerTransferDialog
      open
      onOpenChange={(open) => {
        if (!open) setPending(null)
      }}
      transfer={pending.transfer}
      newOwnerName={membersById?.get(pending.ownerId ?? '')?.display_name ?? 'de nieuwe eigenaar'}
      onConfirm={(transfer) => {
        const ownerId = pending.ownerId
        setPending(null)
        void save({ owner_user_id: ownerId, transfer_agreements: transfer })
      }}
    />
  ) : null
  return { changeOwner, dialog }
}
